// Экраны, на которые ведут ссылки из писем: /reset?token=… и /verify?token=…
//
// Оба живут вне планировщика: пользователь попадает сюда из почты, часто в
// другом браузере, где сессии нет вовсе.
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { Logo } from '../ui/shell';
import { useT } from '../i18n';
import { navigate, useRoute } from '../router';
import { useAuth } from './AuthProvider';
import { resetPassword, verifyEmail } from './api';
import { errorKey, passwordProblemKeys } from './errors';
import type { ApiError } from './api';
import styles from './AuthPages.module.css';

function Shell({ title, children }: { title: string; children: React.ReactNode }): ReactElement {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Logo />
        <h1 className={styles.title}>{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function ResetPage(): ReactElement {
  const t = useT();
  const { query } = useRoute();
  const token = query.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await resetPassword({ token, password });
    setBusy(false);
    if (res.ok) setDone(true);
    else setError(res.error);
  }

  const problems = passwordProblemKeys(error?.problems);

  if (!token) {
    return (
      <Shell title={t('auth.reset.title')}>
        <p className={styles.error}>{t('auth.err.bad_token')}</p>
        <button type="button" className="btn" onClick={() => navigate('/')}>
          {t('auth.toApp')}
        </button>
      </Shell>
    );
  }

  return (
    <Shell title={t('auth.reset.title')}>
      {done ? (
        <>
          <p className={styles.notice}>{t('auth.reset.done')}</p>
          {/* Вход не выдаётся автоматически: сброс закрывает все сессии,
              и следующий шаг — осознанный вход с новым паролем. */}
          <button type="button" className="btn btn-primary" onClick={() => navigate('/?signin=1')}>
            {t('auth.signin')}
          </button>
        </>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit}>
          {error && (
            <p className={styles.error} role="alert">
              {t(errorKey(error))}
              {problems.length > 0 && (
                <ul className={styles.errorList}>
                  {problems.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              )}
            </p>
          )}
          <label className="field-label" htmlFor="reset-password">
            {t('auth.password.new')}
          </label>
          <input
            id="reset-password"
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            required
            autoFocus
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className={styles.hint}>{t('auth.pw.rule')}</p>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('auth.busy') : t('auth.reset.submit')}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyPage(): ReactElement {
  const t = useT();
  const { query } = useRoute();
  const { applyUser } = useAuth();
  const token = query.get('token') ?? '';
  const [state, setState] = useState<'busy' | 'ok' | 'fail'>('busy');

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState('fail');
      return undefined;
    }
    void verifyEmail(token).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        if (res.value?.user) applyUser(res.value.user);
        setState('ok');
      } else {
        setState('fail');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token, applyUser]);

  return (
    <Shell title={t('auth.verify.title')}>
      {state === 'busy' && <p className={styles.hint}>{t('auth.busy')}</p>}
      {state === 'ok' && <p className={styles.notice}>{t('auth.verify.ok')}</p>}
      {state === 'fail' && <p className={styles.error}>{t('auth.verify.fail')}</p>}
      <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
        {t('auth.toApp')}
      </button>
    </Shell>
  );
}
